import { type ComponentPropsWithoutRef, type FC } from 'react';
import { type PortaledSurfaceVariant } from '../lib/portaledSurface';
import { cn } from '../lib/utils';
import { DARK_OVERLAY_SURFACE_CLASSES } from './DarkThemeScope';
import {
  SheetContent as ShadSheetContent,
  SheetFooter as ShadSheetFooter,
  SheetHeader as ShadSheetHeader,
} from './ui/sheet';

type SheetHeaderProps = ComponentPropsWithoutRef<typeof ShadSheetHeader>;
type SheetFooterProps = ComponentPropsWithoutRef<typeof ShadSheetFooter>;
type SheetContentProps = ComponentPropsWithoutRef<typeof ShadSheetContent> & {
  /** Prevents Escape from closing the sheet (in-progress edits, nested shortcuts, etc.). */
  disableEscapeKeyDown?: boolean;
  /** Light (default) or dark surface. Pass `"dark"` for a sheet opened from inside a `DarkThemeScope` — it portals to `document.body` and so escapes the scope's own `.dark`. */
  variant?: PortaledSurfaceVariant;
};

export const SheetContent: FC<SheetContentProps> = ({
  disableEscapeKeyDown,
  onEscapeKeyDown,
  className,
  variant = 'light',
  ...props
}) => {
  return (
    <ShadSheetContent
      {...props}
      onEscapeKeyDown={(e) => {
        if (disableEscapeKeyDown) e.preventDefault();
        onEscapeKeyDown?.(e);
      }}
      className={cn(
        'tw',
        // Built-in close button: position, size, centering
        '[&>button:last-child]:right-6 [&>button:last-child]:top-6',
        '[&>button:last-child]:flex [&>button:last-child]:size-10 [&>button:last-child]:items-center [&>button:last-child]:justify-center',
        '[&>button:last-child]:opacity-100',
        // Icon inside close button: size and color
        '[&>button:last-child>svg:hover]:text-foreground-primary [&>button:last-child>svg]:size-4 [&>button:last-child>svg]:text-foreground-secondary',
        variant === 'dark' && DARK_OVERLAY_SURFACE_CLASSES,
        className,
      )}
    />
  );
};

export const SheetHeader: FC<SheetHeaderProps> = (props) => {
  return (
    <ShadSheetHeader
      {...props}
      className={cn('mr-10 px-6 pb-2 pt-8 text-left font-semibold heading-lg', props.className)}
    />
  );
};

export const SheetFooter: FC<SheetFooterProps> = (props) => {
  return <ShadSheetFooter {...props} className={cn('border-t border-border', props.className)} />;
};

export { Sheet, SheetClose, SheetDescription, SheetTitle, SheetTrigger } from './ui/sheet';
